import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, Paper, Alert } from "@mui/material";
import { getFunctions, httpsCallable } from "firebase/functions";
import MiniMessageInput from "./MiniMessageInput";
import MiniMessages from "./MiniMessages";

const functions = getFunctions();
const proxyStartChatCallable = httpsCallable(functions, "proxyStartChat");
const proxySendMessageCallable = httpsCallable(functions, "proxySendMessage");
const proxyGetMessagesCallable = httpsCallable(functions, "proxyGetMessages");

const MiniChat = () => {
  const [chatId, setChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Function to start a new chat
  const startNewChat = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await proxyStartChatCallable();
      setChatId(response.data.id_chat);
      console.log("Started new chat with ID:", response.data.id_chat);
    } catch (error) {
      console.error("Error starting new chat:", error);
      setError(`Failed to start new chat: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Function to send a message
  const handleSendMessage = async (message) => {
    if (!message.trim()) return;

    try {
      // If no chat exists, start one first
      let currentChatId = chatId;
      if (!currentChatId) {
        setLoading(true);
        const startResponse = await proxyStartChatCallable();
        currentChatId = startResponse.data.id_chat;
        setChatId(currentChatId);
        setLoading(false);
      }

      // Add user message to UI immediately
      const userMessage = {
        message: message,
        direction: "outgoing",
        sender: "user",
      };
      setMessages((prev) => [...prev, userMessage]);
      setInputValue("");
      setIsTyping(true);
      setError("");

      // Send message to AI via Firebase function proxy
      const response = await proxySendMessageCallable({
        id_chat: currentChatId,
        message: message,
      });

      // Add AI message with the complete response
      const aiMessage = {
        message: response.data.response,
        direction: "incoming",
        sender: "assistant",
      };
      setMessages((prev) => [...prev, aiMessage]);

      setIsTyping(false);
    } catch (error) {
      console.error("Error sending message:", error);
      setError(`Failed to send message: ${error.message}`);
      setIsTyping(false);
    }
  };

  // Function to start a new conversation
  const handleNewChat = () => {
    setChatId(null);
    setMessages([]);
    setInputValue("");
    setIsTyping(false);
    setError("");
  };

  return (
    <Paper
      elevation={1}
      sx={{
        mt: 3,
        mb: 3,
        borderRadius: 2,
        overflow: "hidden",
        maxWidth: 800,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#f8f9fa",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          New Chat API Test
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {messages.length > 0 && (
            <button
              onClick={handleNewChat}
              style={{
                padding: "6px 12px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                backgroundColor: "white",
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              New Chat
            </button>
          )}
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {chatId ? `Chat: ${chatId.slice(-8)}` : "No chat started"}
          </Typography>
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Box sx={{ p: 2, backgroundColor: "#ffebee" }}>
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        </Box>
      )}

      {/* Chat Area */}
      <Box sx={{ height: 400, display: "flex", flexDirection: "column" }}>
        {/* Messages */}
        <Box sx={{ flex: 1, overflow: "hidden" }}>
          {messages.length === 0 && !loading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "text.secondary",
              }}
            >
              <Typography variant="body2">
                Send a message to start testing...
              </Typography>
            </Box>
          ) : (
            <MiniMessages messages={messages} isTyping={isTyping} />
          )}
        </Box>

        {/* Input */}
        <Box sx={{ borderTop: "1px solid #e0e0e0", p: 2 }}>
          <MiniMessageInput
            inputValue={inputValue}
            setInputValue={setInputValue}
            handleSendMessage={handleSendMessage}
            disabled={loading || isTyping}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default MiniChat;
